import React from 'react';
import { calculateLastPage } from 'utilities/src/pagination';
import getMetaProps from 'utilities/src/getMetaProps';
import { Links, POSTS_PER_PAGE } from 'utils/shared';
import { parseImageUrl } from 'utils/imageUtils';
import { RankMathSeo } from 'gql/graphql-operations';
import extractText from 'utilities/src/extractText';

const ogStringDataKeys = ['locale', 'type', 'title', 'description', 'url', 'siteName'] as const;
const ogImageStringKeys = ['secureUrl', 'width', 'height', 'type'] as const;
const twitterKeys = ['card', 'title', 'description'] as const;
const articleKeys = ['section', 'publishedTime'] as const;

export function addDefaultValues(data: RankMathSeo, defaultValue?: string) {
  const fullSeoData: RankMathSeo = {
    ...data,
    description: extractText(data.description) || defaultValue,
    openGraph: {
      ...data.openGraph,
      description: extractText(data.openGraph?.description) || defaultValue,
      siteName: data.openGraph?.siteName?.replaceAll('&amp;', '&') || defaultValue,
      title: extractText(data.openGraph?.title?.replaceAll('&amp;', '&')) || defaultValue,
      type: data.openGraph?.type || defaultValue,
      url: data.openGraph?.url || defaultValue,
      twitterMeta: {
        ...data.openGraph?.twitterMeta,
        card: data.openGraph?.twitterMeta?.card,
        description: extractText(data.openGraph?.twitterMeta?.description) || defaultValue,
        title: extractText(data.openGraph?.twitterMeta?.title?.replaceAll('&amp;', '&')) || defaultValue,
      },
    },
  };
  return fullSeoData;
}

export function metaTags(data: RankMathSeo & { ogImageAlt?: string }) {
  let robots: any[] = [];

  if (process.env.NEXT_PUBLIC_ENABLE_ROBOTS === 'true') {
    if (data.robots) {
      robots = [{ name: 'robots', content: data.robots.join(', ') }];
    }
  } else {
    robots = [
      {
        name: 'robots',
        content: 'nofollow, noindex, noarchive, noimageindex, nosnippet',
      },
    ];
  }

  const metaTagsProps = [
    ...(data.description ? [{ name: 'description', content: extractText(data.description) }] : []),
    ...robots,
    ...(data.ogImageAlt ? [{ property: 'og:image:alt', content: data.ogImageAlt }] : []),
    ...getMetaProps(data.openGraph, ogStringDataKeys),
    ...(data.openGraph?.image?.url
      ? [{ property: 'og:image', content: parseImageUrl(data.openGraph.image.url) }]
      : []),
    ...getMetaProps(data.openGraph?.articleMeta, articleKeys, 'article'),
    ...getMetaProps(data.openGraph?.image, ogImageStringKeys, 'og:image'),
    ...getMetaProps(data.openGraph?.twitterMeta, twitterKeys, 'twitter', 'name'),
    ...(data.openGraph?.slackEnhancedData?.reduce((acc: Record<string, string>[], curr, index) => {
      acc.push(
        { name: `twitter:label${index + 1}`, content: curr?.label ?? '' },
        { name: `twitter:data${index + 1}`, content: curr?.data ?? '' },
      );
      return acc;
    }, []) ?? []),
    // Once we get rid of parseImageUrl, this can go back to twitter twitterKeys/getMetaProps
    ...(data.openGraph?.twitterMeta?.image
      ? [{ name: 'twitter:image', content: parseImageUrl(data.openGraph?.twitterMeta?.image ?? '') }]
      : []),
  ];
  return metaTagsProps.reduce((acc: JSX.Element[], metaProps) => {
    // eslint-disable-next-line react/jsx-props-no-spreading
    acc.push(<meta {...metaProps} key={metaProps.name || metaProps.property} />);
    return acc;
  }, []);
}

export function linkTags(links: Links) {
  return links.map((link) => <link href={link.href} rel={link.rel} key={link.rel} />);
}

export function getPaginationLinks(asPath: string, page: number, total: number, host: string): Links {
  const hasPrev = page > 1;
  const hasNext = page < calculateLastPage(total, POSTS_PER_PAGE);
  const routeMatches = asPath.match(/(.*)\/page\/\d*(.)*/);
  if (!routeMatches) {
    return [...(hasNext ? [{ href: `${host}${asPath}page/2/`, rel: 'next' }] : [])];
  }
  return [
    ...(hasNext
      ? [{ href: `${host}${routeMatches[1] ?? ''}/page/${page + 1}${routeMatches[2] ?? ''}`, rel: 'next' }]
      : []),
    ...(hasPrev
      ? [{ href: `${host}${routeMatches[1] ?? ''}/page/${page - 1}${routeMatches[2] ?? ''}`, rel: 'prev' }]
      : []),
  ];
}

export function removeMetaTags(metaElements: JSX.Element[]) {
  metaElements.forEach((element) => {
    const metaTag = document.querySelector(
      `meta[name='${element.props.name}'], meta[property='${element.props.property}']`,
    );
    metaTag?.parentNode?.removeChild(metaTag);
  });
}
